/**
 * RFC-114 Phase 2 — Microscope / Telescope scope navigation buttons.
 *
 * Rendered next to the breadcrumb strip in the top-right HUD overlay.
 * Microscope animates the camera closer (drill down one LoR level).
 * Telescope animates the camera further (pull back one LoR level).
 *
 * No crossfade / opacity animation here — that is Phase 3.
 * The camera animation is delegated to Giro3DAdapter.animateCameraDistance().
 */
import type { CSSProperties } from 'react';
import { tokens } from '@/design';
import type { LoRLevel } from '@/engines/giro3d/lorController';

export interface LoRScopeButtonsProps {
  readonly currentLoR: LoRLevel;
  readonly onMicroscope: () => void;
  readonly onTelescope: () => void;
  /** When true, enables the microscope button even at LoR3 (interior scope available). */
  readonly canDrillInterior?: boolean;
}

export function LoRScopeButtons({ currentLoR, onMicroscope, onTelescope, canDrillInterior }: LoRScopeButtonsProps) {
  const microscopeDisabled = currentLoR >= 3 && !canDrillInterior;
  const telescopeDisabled = currentLoR <= 0;

  return (
    <div style={containerStyle} role="toolbar" aria-label="LoR scope navigation">
      <ScopeButton
        icon="zoom_in"
        label="Zoom into detail (Microscope)"
        disabledLabel="Already at the deepest zoom level"
        disabled={microscopeDisabled}
        onClick={onMicroscope}
        data-testid="lor-scope-microscope"
      />
      <ScopeButton
        icon="zoom_out"
        label="Zoom out to context (Telescope)"
        disabledLabel="Already at the widest zoom level"
        disabled={telescopeDisabled}
        onClick={onTelescope}
        data-testid="lor-scope-telescope"
      />
    </div>
  );
}

interface ScopeButtonProps {
  readonly icon: string;
  readonly label: string;
  readonly disabledLabel: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly 'data-testid'?: string;
}

function ScopeButton({ icon, label, disabledLabel, disabled, onClick, 'data-testid': testId }: ScopeButtonProps) {
  return (
    <button
      type="button"
      style={disabled ? disabledButtonStyle : buttonStyle}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      aria-label={label}
      title={disabled ? disabledLabel : label}
      data-testid={testId}
    >
      <span className="material-symbols-outlined" style={iconStyle} aria-hidden="true">
        {icon}
      </span>
    </button>
  );
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: tokens.space[1],
  pointerEvents: 'auto',
};

const buttonBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: tokens.space[5] + tokens.space[1],
  height: tokens.space[5] + tokens.space[1],
  borderRadius: tokens.radius.sm,
  border: 'none',
  cursor: 'pointer',
  background: tokens.color.surface[3],
  color: tokens.color.ink.primary,
  padding: 0,
  transition: `opacity ${tokens.motion.duration.fast}ms ease`,
};

const buttonStyle: CSSProperties = {
  ...buttonBase,
  opacity: 1,
};

const disabledButtonStyle: CSSProperties = {
  ...buttonBase,
  opacity: 0.35,
  cursor: 'not-allowed',
};

const iconStyle: CSSProperties = {
  fontSize: tokens.font.size.lg,
  lineHeight: 1,
  userSelect: 'none',
};
